import React, { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import type { StrategyTrade } from '@/services/strategyService';
import { SignalChips } from '@/components/automation/TradeAuditDetail';
import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';
import { fmtNY, fmtHold, fmtMoney, exitLabel, pnlColor } from './tradeViewerUtils';

interface MinuteBar {
  timestamp: string;
  symbol: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

// barsDisabled: the candlestick needs the authenticated /api/TimescaleDb/bars
// endpoint, which a public shared-link viewer can't reach. When set, the bar
// fetch and chart render are skipped; the trade facts + events (both pure trade
// data, no API) still render.
interface Props { trade: StrategyTrade; barsDisabled?: boolean; }

/** One labelled fact in the trade header strip. */
const Fact: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className }) => (
  <div className={className}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-sm font-semibold tabular-nums leading-tight">{children}</div>
  </div>
);

export const TradeDetailPane: React.FC<Props> = ({ trade, barsDisabled }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartApiRef = useRef<any>(null);
  const [bars, setBars] = useState<MinuteBar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch bars whenever the selected trade changes
  useEffect(() => {
    // Shared (unauthenticated) viewer — the bars endpoint requires auth, so
    // don't even attempt the request.
    if (barsDisabled) { setBars([]); return; }
    let cancelled = false;
    const fetchBars = async () => {
      setLoading(true); setError(null);
      try {
        const entryT = new Date(trade.entryTime);
        const exitT = trade.exitTime ? new Date(trade.exitTime) : new Date(entryT.getTime() + 60 * 60 * 1000);
        // 4h before / 2h after — wide setup context, sparse-data periods just render as gaps
        const fromMs = entryT.getTime() - 4 * 60 * 60 * 1000;
        const toMs = exitT.getTime() + 2 * 60 * 60 * 1000;
        // No `source`: the API serves one logical bar series and picks the store itself. This
        // pane used to pin the bulk store, which holds no forex or crypto by construction — so
        // every FX trade rendered "No bars in range" however much data we had.
        const url = `${API_BASE}/api/TimescaleDb/bars/${trade.symbol}/timescaledb?period=Minute&timeUnits=1&from=${encodeURIComponent(new Date(fromMs).toISOString())}&to=${encodeURIComponent(new Date(toMs).toISOString())}`;
        const resp = await apiFetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data: MinuteBar[] = await resp.json();
        if (!cancelled) setBars(data);
      } catch (err: any) {
        if (!cancelled) { setError(err.message || String(err)); setBars([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBars();
    return () => { cancelled = true; };
  }, [trade.id, trade.symbol, trade.entryTime, trade.exitTime, barsDisabled]);

  // Render chart whenever bars or trade changes
  useEffect(() => {
    if (!containerRef.current) return;
    if (!bars.length) return;
    let cancelled = false;

    (async () => {
      const lc = await import('lightweight-charts');
      const { createChart, CandlestickSeries, createSeriesMarkers } = lc as any;
      if (cancelled || !containerRef.current) return;

      // Tear down previous chart
      if (chartApiRef.current) {
        try { chartApiRef.current.chart.remove(); } catch {}
        chartApiRef.current = null;
      }

      const isDark = document.documentElement.classList.contains('dark');
      const chart = createChart(containerRef.current, {
        autoSize: true,
        layout: { background: { color: 'transparent' }, textColor: isDark ? '#d1d4dc' : '#333' },
        grid: {
          vertLines: { color: isDark ? 'rgba(197,203,206,0.1)' : 'rgba(197,203,206,0.2)' },
          horzLines: { color: isDark ? 'rgba(197,203,206,0.1)' : 'rgba(197,203,206,0.2)' },
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          borderColor: isDark ? 'rgba(197,203,206,0.3)' : '#ccc',
          tickMarkFormatter: (time: number) => {
            const d = new Date(time * 1000);
            return new Intl.DateTimeFormat('en-US', {
              timeZone: 'America/New_York',
              hour: '2-digit', minute: '2-digit', hour12: false,
            }).format(d);
          },
        },
        rightPriceScale: { borderColor: isDark ? 'rgba(197,203,206,0.3)' : '#ccc' },
        crosshair: { mode: 0 },
        localization: {
          timeFormatter: (time: number) => {
            const d = new Date(time * 1000);
            return new Intl.DateTimeFormat('en-US', {
              timeZone: 'America/New_York',
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit', hour12: false,
            }).format(d) + ' ET';
          },
        },
      });

      const candleSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#26a69a', downColor: '#ef5350',
        borderUpColor: '#26a69a', borderDownColor: '#ef5350',
        wickUpColor: '#26a69a', wickDownColor: '#ef5350',
      });
      candleSeries.setData(bars.map(b => ({
        time: Math.floor(new Date(b.timestamp).getTime() / 1000) as any,
        open: b.open, high: b.high, low: b.low, close: b.close,
      })));

      // Markers: entry + exit
      const isLong = trade.direction === 'Long';
      const isWin = (trade.realizedPnL || 0) > 0;
      const markers: any[] = [{
        time: Math.floor(new Date(trade.entryTime).getTime() / 1000),
        position: isLong ? 'belowBar' : 'aboveBar',
        color: '#58a6ff',
        shape: isLong ? 'arrowUp' : 'arrowDown',
        text: `${isLong ? 'LONG' : 'SHORT'} @ ${trade.entryPrice.toFixed(3)}`,
      }];
      if (trade.exitTime && trade.exitPrice != null) {
        markers.push({
          time: Math.floor(new Date(trade.exitTime).getTime() / 1000),
          position: isLong ? 'aboveBar' : 'belowBar',
          color: isWin ? '#26a69a' : '#ef5350',
          shape: 'circle',
          text: `${exitLabel(trade)} @ ${trade.exitPrice.toFixed(3)} (${isWin ? '+' : ''}${(trade.realizedPnL || 0).toFixed(0)})`,
        });
      }
      createSeriesMarkers(candleSeries, markers);

      // Price lines
      candleSeries.createPriceLine({
        price: trade.initialStopPrice, color: '#ff9800', lineWidth: 1, lineStyle: 2,
        axisLabelVisible: true, title: 'init stop',
      });
      if (trade.currentStopPrice && trade.currentStopPrice !== trade.initialStopPrice) {
        candleSeries.createPriceLine({
          price: trade.currentStopPrice, color: '#ffeb3b', lineWidth: 1, lineStyle: 2,
          axisLabelVisible: true, title: 'final stop',
        });
      }
      if (trade.initialTakeProfitPrice) {
        candleSeries.createPriceLine({
          price: trade.initialTakeProfitPrice, color: '#4caf50', lineWidth: 1, lineStyle: 2,
          axisLabelVisible: true, title: 'TP',
        });
      }
      candleSeries.createPriceLine({
        price: trade.entryPrice, color: '#58a6ff', lineWidth: 1, lineStyle: 0,
        axisLabelVisible: true, title: 'entry',
      });

      chart.timeScale().fitContent();
      chartApiRef.current = { chart, candleSeries };
    })();

    return () => {
      cancelled = true;
      if (chartApiRef.current) {
        try { chartApiRef.current.chart.remove(); } catch {}
        chartApiRef.current = null;
      }
    };
  }, [bars, trade.id]);

  const isLong = trade.direction === 'Long';
  const pnl = trade.realizedPnL || 0;
  const isWin = pnl > 0;

  return (
    <>
      {/* Trade headline — symbol + direction + outcome, then the facts strip */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-3">
          <span className="text-lg font-bold tracking-tight">{trade.symbol}</span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            isLong ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-500'}`}>
            {isLong ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {trade.direction}
          </span>
          <span className={`text-lg font-bold tabular-nums ${pnlColor(pnl)}`}>{fmtMoney(pnl, true)}</span>
          <span className="text-sm text-muted-foreground tabular-nums">
            {trade.realizedRMultiple != null ? `${trade.realizedRMultiple.toFixed(2)}R` : ''}
          </span>
          <span className={`ml-auto rounded px-2 py-0.5 text-[11px] font-medium ${
            isWin ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-500'}`}>
            {exitLabel(trade)}
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-2.5">
          <Fact label="Entry">
            {trade.entryPrice.toFixed(3)}
            <span className="block text-[11px] font-normal text-muted-foreground">{fmtNY(trade.entryTime)} ET</span>
          </Fact>
          <Fact label="Exit">
            {trade.exitPrice?.toFixed(3) ?? '-'}
            <span className="block text-[11px] font-normal text-muted-foreground">
              {trade.exitTime ? `${fmtNY(trade.exitTime)} ET` : 'still open'}
            </span>
          </Fact>
          <Fact label="Hold">{fmtHold(trade.holdingSeconds)}</Fact>
          <Fact label="Qty">{trade.quantity}</Fact>
          <Fact label="Stop">
            {trade.initialStopPrice.toFixed(3)}
            {trade.currentStopPrice !== trade.initialStopPrice && (
              <span className="block text-[11px] font-normal text-amber-500">→ {trade.currentStopPrice.toFixed(3)}</span>
            )}
          </Fact>
          <Fact label="MFE / MAE">
            <span className="text-emerald-500">{trade.maxFavorableExcursion?.toFixed(2) ?? '-'}</span>
            {' / '}
            <span className="text-red-500">{trade.maxAdverseExcursion?.toFixed(2) ?? '-'}</span>
          </Fact>
        </div>

        {trade.exitReason && (
          <div className="mt-2.5 pt-2 border-t text-[11px] text-muted-foreground">
            Raw exit reason: <span className="text-foreground/80">{trade.exitReason}</span>
          </div>
        )}
      </Card>

      {(trade.entryReason || trade.events?.some(e => e.type === 'Entry' && e.signal)) && (
        <Card className="p-3 space-y-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Entry signal</div>
          {trade.entryReason && <div className="text-xs break-words">{trade.entryReason}</div>}
          {(() => {
            const sig = trade.events?.find(e => e.type === 'Entry')?.signal;
            return sig && Object.keys(sig).length > 0 ? <SignalChips signal={sig} /> : null;
          })()}
        </Card>
      )}

      <Card className="relative shrink-0 h-[340px] lg:h-[380px]">
        {barsDisabled ? (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm text-center px-4">
            Candlestick chart isn't available in the shared view —
            trade details and the event log are shown above.
          </div>
        ) : (
          <>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm z-10">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading bars…
              </div>
            )}
            {error && !loading && (
              <div className="absolute inset-0 flex items-center justify-center text-red-500 text-sm">
                Failed to fetch bars: {error}
              </div>
            )}
            {!loading && !error && bars.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
                No bars in range — check the trade's date is covered by our historical data.
              </div>
            )}
            <div ref={containerRef} className="w-full h-full" />
          </>
        )}
      </Card>

      {trade.events && trade.events.length > 0 && (
        <Card className="p-2 text-[11px] shrink-0 max-h-[220px] overflow-y-auto">
          <div className="grid grid-cols-[150px_120px_70px_85px_1fr] gap-2 font-semibold border-b pb-1 mb-1 sticky top-0 bg-card">
            <span>Time (ET)</span>
            <span>Type</span>
            <span>Bar Px</span>
            <span>New Stop</span>
            <span>Notes</span>
          </div>
          {trade.events.map((ev, i) => (
            <React.Fragment key={i}>
              <div className="grid grid-cols-[150px_120px_70px_85px_1fr] gap-2 py-0.5">
                <span className="tabular-nums">{fmtNY(ev.time, true)}</span>
                <span className="text-blue-400">{ev.type}</span>
                <span className="tabular-nums">{ev.price?.toFixed(3) ?? '-'}</span>
                <span className="tabular-nums text-orange-400 font-semibold">{ev.stopPrice?.toFixed(3) ?? '-'}</span>
                <span className="text-muted-foreground">{ev.notes ?? ''}</span>
              </div>
              {ev.signal && Object.keys(ev.signal).length > 0 && (
                <div className="pl-2 pb-1"><SignalChips signal={ev.signal} /></div>
              )}
            </React.Fragment>
          ))}
        </Card>
      )}
    </>
  );
};

export default TradeDetailPane;
